import { useCallback, useEffect, useRef, useState } from 'react';
import type { PatientAgentContext } from '../../models/patientAgentContext';
import { patientAgentContextService } from '../../services/patientAgentContextService';
import { onPatientContextChanged } from '../../services/patientAgentContextEvents';

// A patient's agent context, kept fresh when it changes elsewhere in the app
// (the other editor, or an accepted update_patient_context proposal).
export function usePatientAgentContext(patientId: number | null) {
  const [context, setContext] = useState<PatientAgentContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const seqRef = useRef(0);

  const reload = useCallback(async () => {
    if (patientId == null) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(false);
    try {
      const next = await patientAgentContextService.get(patientId);
      if (seqRef.current === seq) setContext(next);
    } catch (err) {
      console.warn('usePatientAgentContext: failed to load', err);
      if (seqRef.current === seq) setError(true);
    } finally {
      if (seqRef.current === seq) setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setContext(null);
    reload();
  }, [reload]);

  useEffect(
    () =>
      onPatientContextChanged((id) => {
        if (id === patientId) reload();
      }),
    [patientId, reload],
  );

  return { context, setContext, loading, error, reload };
}
